import { useCallback, useEffect, useRef } from "react";
import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import useForcePushStream, {
  ForcePushCompletedEvent,
} from "../../../hooks/services/useForcePushStream";
import {
  RESYNC_TOAST_ERROR_DURATION_MS,
  RESYNC_TOAST_INFO_DURATION_MS,
  RESYNC_TOAST_SUCCESS_DURATION_MS,
} from "../../../utils/resync/itemResync/itemResyncUtils";

/**
 * How long to wait for the socket completion before giving up on it and reconciling from
 * the server anyway. Orchestration serialises the jobs behind whatever else is in flight for
 * the same items, so this is generous; file/doc columns re-upload per linked item on top.
 */
export const FORCE_PUSH_FALLBACK_MS = 45_000;
export const FORCE_PUSH_SLOW_FALLBACK_MS = 120_000;

export const FORCE_PUSH_TIMED_OUT_MESSAGE =
  "This is taking longer than expected. Your item view has been refreshed.";
export const FORCE_PUSH_RECONNECTED_MESSAGE =
  "Connection was briefly interrupted. Your item view has been refreshed.";

interface PendingForcePush {
  columnKeys: Set<string>;
  timeoutId: ReturnType<typeof setTimeout>;
}

interface UseForcePushTrackerArgs {
  userId: string | number | undefined;
  accountId?: string | number;
  /** Toggles the in-progress spinner on the discrepancy icon of the given column keys. */
  setColumnsResyncing: (columnKeys: string[], isResyncing: boolean) => void;
  /** Reconciles the optimistic item view with server truth. */
  revalidate: () => Promise<void>;
}

export type RegisterForcePush = (
  requestId: string,
  columnKeys: string[],
  hasSlowColumn: boolean,
  /** Overrides the fallback wait, for clicks that queue far more than one job per column. */
  timeoutMs?: number,
) => void;

/**
 * Tracks item-view resyncs that were queued through orchestration (backend answered 202)
 * until their socket completions arrive.
 *
 * The item view stays optimistic in the meantime: the discrepancy badge is already cleared
 * and the icon shows a spinner for every queued column. Each completion drops that column's
 * spinner and toasts its outcome; the last one triggers the revalidation. A fallback timer
 * and the socket's reconnect both reconcile from the server in case a completion never
 * arrives (room emits are not buffered).
 */
const useForcePushTracker = ({
  userId,
  accountId,
  setColumnsResyncing,
  revalidate,
}: UseForcePushTrackerArgs) => {
  const pendingRef = useRef<Map<string, PendingForcePush>>(new Map());

  // Latest callbacks in refs, so the handlers below stay stable across renders.
  const setColumnsResyncingRef = useRef(setColumnsResyncing);
  const revalidateRef = useRef(revalidate);
  setColumnsResyncingRef.current = setColumnsResyncing;
  revalidateRef.current = revalidate;

  const settle = useCallback((requestId: string): PendingForcePush | undefined => {
    const pending = pendingRef.current.get(requestId);
    if (!pending) return undefined;

    clearTimeout(pending.timeoutId);
    pendingRef.current.delete(requestId);

    return pending;
  }, []);

  const registerForcePush = useCallback<RegisterForcePush>(
    (requestId, columnKeys, hasSlowColumn, timeoutMs) => {
      const keys = new Set(columnKeys);
      if (keys.size === 0) return;

      setColumnsResyncingRef.current([...keys], true);

      const timeoutId = setTimeout(
        () => {
          const pending = settle(requestId);
          if (!pending) return;

          setColumnsResyncingRef.current([...pending.columnKeys], false);
          MondayApiQueryInstance.mondayToastNotification(
            FORCE_PUSH_TIMED_OUT_MESSAGE,
            "info",
            RESYNC_TOAST_INFO_DURATION_MS,
          );
          void revalidateRef.current();
        },
        timeoutMs ?? (hasSlowColumn ? FORCE_PUSH_SLOW_FALLBACK_MS : FORCE_PUSH_FALLBACK_MS),
      );

      pendingRef.current.set(requestId, { columnKeys: keys, timeoutId });
    },
    [settle],
  );

  const onCompleted = useCallback(
    (event: ForcePushCompletedEvent) => {
      const pending = pendingRef.current.get(event?.requestId);
      // Not a click made from this item view, or one already reconciled by the fallback.
      if (!pending) return;

      pending.columnKeys.delete(event.columnKey);
      setColumnsResyncingRef.current([event.columnKey], false);

      const failed = (event?.summary?.dead ?? 0) > 0;
      const toastType = event?.reason === "no_sync_group" ? "info" : failed ? "error" : "success";
      MondayApiQueryInstance.mondayToastNotification(
        event?.message || `${event?.columnTitle ?? event.columnKey} resynced`,
        toastType,
        failed ? RESYNC_TOAST_ERROR_DURATION_MS : RESYNC_TOAST_SUCCESS_DURATION_MS,
      );

      if (pending.columnKeys.size === 0) {
        settle(event.requestId);
        void revalidateRef.current();
      }
    },
    [settle],
  );

  const onReconnect = useCallback(() => {
    if (pendingRef.current.size === 0) return;

    const keys = [...pendingRef.current.values()].flatMap((pending) => [...pending.columnKeys]);
    [...pendingRef.current.keys()].forEach(settle);

    setColumnsResyncingRef.current(keys, false);
    MondayApiQueryInstance.mondayToastNotification(
      FORCE_PUSH_RECONNECTED_MESSAGE,
      "info",
      RESYNC_TOAST_INFO_DURATION_MS,
    );
    void revalidateRef.current();
  }, [settle]);

  useForcePushStream({ userId, accountId, onCompleted, onReconnect, enabled: !!userId });

  // Nothing should fire into an unmounted item view.
  useEffect(() => {
    const pendingAtMount = pendingRef.current;
    return () => {
      pendingAtMount.forEach((pending) => clearTimeout(pending.timeoutId));
      pendingAtMount.clear();
    };
  }, []);

  return { registerForcePush };
};

export default useForcePushTracker;
